import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import MergeIcon from '@mui/icons-material/MergeType';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useRTL } from '@hooks/useIsRTL';

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '24px',
    maxWidth: '520px',
    width: '100%',
    padding: '0',
    margin: '16px',
  },
});

const CloseButtonContainer = styled(Box)({
  position: 'absolute',
  top: '16px',
  right: '16px',
});

const ContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '48px 32px 32px',
});

const IconContainer = styled(Box)({
  width: '64px',
  height: '64px',
  borderRadius: '16px',
  backgroundColor: '#f3f0ff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '24px',
});

const Title = styled(Typography)({
  fontSize: '20px',
  fontWeight: 700,
  color: '#262626',
  marginBottom: '12px',
  textAlign: 'center',
});

const Description = styled(Typography)({
  fontSize: '14px',
  color: '#666666',
  marginBottom: '24px',
  textAlign: 'center',
  lineHeight: '22px',
});

const WhenToUseContainer = styled(Box)({
  width: '100%',
  backgroundColor: '#faf5ff',
  borderRadius: '12px',
  padding: '16px',
  marginBottom: '16px',
  border: '1px solid #f3e8ff',
});

const WhenToUseTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#262626',
  marginBottom: '12px',
});

const WhenToUseItem = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  marginBottom: '8px',
  '&:last-child': {
    marginBottom: 0,
  },
});

const WhenToUseText = styled(Typography)({
  fontSize: '13px',
  color: '#666666',
  lineHeight: '20px',
});

const InfoBanner = styled(Box)({
  width: '100%',
  backgroundColor: 'rgba(0, 106, 255, 0.08)',
  borderRadius: '12px',
  padding: '12px 16px',
  marginBottom: '24px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  border: '1px solid rgba(0, 106, 255, 0.2)',
});

const InfoText = styled(Typography)({
  fontSize: '13px',
  color: '#003d99',
  lineHeight: '20px',
  flex: 1,
});

const StartButton = styled(Button)({
  width: '100%',
  backgroundColor: '#5b3fff',
  color: '#ffffff',
  padding: '14px 24px',
  borderRadius: '12px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  '&:hover': {
    backgroundColor: '#4a2fe8',
  },
});

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
  onStart: () => void;
}

const OnboardingModal: React.FC<OnboardingModalProps> = memo(({ open, onClose, onStart }) => {
  const { t } = useTranslation();
  const { isRTL } = useRTL();

  const handleStart = () => {
    onStart();
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      <CloseButtonContainer>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ color: '#666666' }} />
        </IconButton>
      </CloseButtonContainer>
      <DialogContent sx={{ padding: 0 }}>
        <ContentContainer>
          <IconContainer>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.1063 2.90661C16.7589 2.74815 16.3815 2.66614 15.9996 2.66614C15.6178 2.66614 15.2404 2.74815 14.893 2.90661L3.46629 8.10661C3.22969 8.21094 3.02853 8.38181 2.88731 8.59842C2.74609 8.81503 2.6709 9.06803 2.6709 9.32661C2.6709 9.58519 2.74609 9.83819 2.88731 10.0548C3.02853 10.2714 3.22969 10.4423 3.46629 10.5466L14.9063 15.7599C15.2537 15.9184 15.6311 16.0004 16.013 16.0004C16.3948 16.0004 16.7722 15.9184 17.1196 15.7599L28.5596 10.5599C28.7962 10.4556 28.9974 10.2847 29.1386 10.0681C29.2798 9.85153 29.355 9.59853 29.355 9.33995C29.355 9.08137 29.2798 8.82837 29.1386 8.61176C28.9974 8.39514 28.7962 8.22427 28.5596 8.11995L17.1063 2.90661Z" stroke="#5B2FF9" stroke-width="2.66667" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2.667 16C2.66637 16.255 2.73888 16.5049 2.87595 16.7199C3.01302 16.935 3.20889 17.1062 3.44033 17.2133L14.907 22.4267C15.2526 22.5832 15.6276 22.6641 16.007 22.6641C16.3864 22.6641 16.7614 22.5832 17.107 22.4267L28.547 17.2267C28.783 17.1206 28.9831 16.9481 29.1227 16.7303C29.2624 16.5125 29.3357 16.2587 29.3337 16" stroke="#5B2FF9" stroke-width="2.66667" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2.667 22.6666C2.66637 22.9216 2.73888 23.1715 2.87595 23.3866C3.01302 23.6016 3.20889 23.7729 3.44033 23.88L14.907 29.0933C15.2526 29.2498 15.6276 29.3307 16.007 29.3307C16.3864 29.3307 16.7614 29.2498 17.107 29.0933L28.547 23.8933C28.783 23.7872 28.9831 23.6147 29.1227 23.3969C29.2624 23.1791 29.3357 22.9254 29.3337 22.6666" stroke="#5B2FF9" stroke-width="2.66667" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>

          </IconContainer>
          
          <Title>
            {t('CHANNELS.UNITS_MAPPING.MODAL_TITLE', 'ما هو دمج الوحدات؟')}
          </Title>
          
          <Description>
            {t('CHANNELS.UNITS_MAPPING.MODAL_DESCRIPTION', 'دمج الوحدات يسمح لك بربط نفس الوحدة الموجودة على منصات مختلفة (مثل Airbnb وBooking وGathern) في وحدة واحدة موحدة داخل مسؤول.')}
          </Description>

          <WhenToUseContainer>
            <WhenToUseTitle>
              {t('CHANNELS.UNITS_MAPPING.MODAL_WHEN_TO_USE_TITLE', 'متى تستخدم دمج الوحدات؟')}
            </WhenToUseTitle>
            
            <WhenToUseItem>
              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#5b3fff', mt: '2px' }} />
              <WhenToUseText>
                {t('CHANNELS.UNITS_MAPPING.MODAL_WHEN_TO_USE_1', 'عندما تكون نفس الوحدة معروضة على أكثر من منصة')}
              </WhenToUseText>
            </WhenToUseItem>
            
            <WhenToUseItem>
              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#5b3fff', mt: '2px' }} />
              <WhenToUseText>
                {t('CHANNELS.UNITS_MAPPING.MODAL_WHEN_TO_USE_2', 'لإدارة التقويم والأسعار من مكان واحد')}
              </WhenToUseText>
            </WhenToUseItem>
            
            <WhenToUseItem>
              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#5b3fff', mt: '2px' }} />
              <WhenToUseText>
                {t('CHANNELS.UNITS_MAPPING.MODAL_WHEN_TO_USE_3', 'لتجنب الحجوزات المزدوجة')}
              </WhenToUseText>
            </WhenToUseItem>
          </WhenToUseContainer>

          <InfoBanner>
            <InfoOutlinedIcon sx={{ fontSize: 20, color: '#006aff' }} />
            <InfoText>
              {t('CHANNELS.UNITS_MAPPING.MODAL_INFO_TEXT', 'بعد دمج الوحدات، سيتم مزامنة التوفر والحجوزات تلقائياً بين جميع المنصات المرتبطة.')}
            </InfoText>
          </InfoBanner>

          <StartButton onClick={handleStart}>
            {t('CHANNELS.UNITS_MAPPING.MODAL_START_BUTTON', 'ابدأ الآن')}
            {isRTL ? (
              <ArrowBackIcon sx={{ fontSize: 20 }} />
            ) : (
              <ArrowForwardIcon sx={{ fontSize: 20 }} />
            )}
          </StartButton>
        </ContentContainer>
      </DialogContent>
    </StyledDialog>
  );
});

OnboardingModal.displayName = 'OnboardingModal';

export default OnboardingModal;
